import { useRef, useState } from 'react';

import LoadingTable from 'components/UI/loading/LoadingTable';
import ProjectTableFooter from 'components/Sherpa/Projects/Project/Table/ProjectTableFooter';

import { LocationMarkerIcon } from '@heroicons/react/outline';
import { MapIcon, ViewListIcon, XIcon } from '@heroicons/react/solid';
import { useLocation } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { getAddressDecode, IGetAddressDecodeResData } from 'api/misc';
import GoogleMap from 'components/UI/GoogleMap';
import LocationMarker from 'components/UI/GoogleMap/LocationMarker';
import { Link } from 'react-router-dom';

interface HowTableHeaderInterface {
  name: string;
  customClassName: string;
}

export default function HouseOfWorshipProjectsWidget({
  data,
}: {
  data: { id: string; howName: string; address: string }[];
}) {
  const howTableRef = useRef<HTMLDivElement | null>(null);
  const [view, setView] = useState<string>('list');
  const perPageHandler = () => {};
  const onPageChangeHandler = () => {};

  const tableHeader = [
    { name: 'HOW Name', customClassName: 'w-1/3' },
    { name: 'Address', customClassName: 'w-1/2' },
  ];

  const [selectedHowProject, setSelectedHowProject] = useState<{
    id: string;
    howName: string;
    address: string;
  }>();
  const location = useLocation();

  const fetchHowProjectGetLocation = useQueries({
    queries: data?.map(
      (d: { id: string; howName: string; address: string }) => {
        return {
          queryKey: ['GET_HOW_PROJECT_GEO_CODE', d],
          queryFn: () => getAddressDecode(d.address),
          select: (resData: { data: IGetAddressDecodeResData }) => {
            const { lat, lng } = resData.data;
            return { ...d, coordinates: { lat, lng } };
          },
        };
      }
    ),
  });

  const howProjectGeoLocated = fetchHowProjectGetLocation?.map((d) => d.data);

  return (
    <section className='mt-8'>
      <div
        ref={howTableRef}
        id='how-table'
        className='rounded-md bg-white w-full'
      >
        <div className='border-b py-3 flex justify-between items-center'>
          <h3 className='font-bold text-bold border-l-4 p-4 border-green-500 capitalize'>
            House Of Worship Projects
          </h3>
          <div className='px-3 flex gap-4'>
            {view === 'list' && (
              <span
                className='hover:text-neutralColor-light cursor-pointer'
                onClick={() => setView('map')}
              >
                <MapIcon className='h-6 w-6' />
                <p className='text-xs'>Map</p>
              </span>
            )}
            {view === 'map' && (
              <span
                className='hover:text-neutralColor-light cursor-pointer'
                onClick={() => setView('list')}
              >
                <ViewListIcon className='h-6 w-6' />
                <p className='text-xs'>List</p>
              </span>
            )}
            {/* <form className='w-full flex md:ml-0' action='#' method='GET'>
              <SearchBar />
            </form> */}
          </div>
        </div>

        {false && <LoadingTable />}
        {/* table header */}
        {view === 'list' && (
          <div className='w-full overflow-x-auto'>
            <table className='w-full'>
              <thead>
                <tr className='flex px-5 py-4 bg-gray-200'>
                  {tableHeader.map((head: HowTableHeaderInterface) => {
                    return (
                      <th
                        key={head.name}
                        className={`flex gap-2 text-neutralColor-normal text-left text-xs uppercase ${head.customClassName}`}
                      >
                        {head.name}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.map((how: any) => (
                  <tr
                    key={how.id}
                    className={
                      'flex items-center w-full px-5 py-4 border-b border-gray-200 text-sm hover:bg-green-50 cursor-pointer bg-white'
                    }
                  >
                    <td className='w-1/3'>{how?.howName}</td>
                    <td className='w-1/2'>{how?.address}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {view === 'map' && (
          <div className='col-span-6 w-full border-l p-5'>
            {!fetchHowProjectGetLocation[0]?.isFetching &&
              howProjectGeoLocated[0] && (
                <GoogleMap
                  defaultCenter={{
                    lat: howProjectGeoLocated[0].coordinates.lat,
                    lng: howProjectGeoLocated[0].coordinates.lng,
                  }}
                >
                  {howProjectGeoLocated?.map((howProject) => {
                    return (
                      <LocationMarker
                        key={howProject?.id}
                        lat={howProject?.coordinates?.lat}
                        lng={howProject?.coordinates?.lng}
                      >
                        <div className='relative'>
                          {selectedHowProject &&
                            selectedHowProject?.id === howProject?.id && (
                              <div
                                onClick={() => setSelectedHowProject(undefined)}
                                className='bg-white border rounded absolute p-3 bottom-4 left-5 w-56'
                              >
                                <XIcon className='h-4 w-4 absolute right-1 top-1 text-neutralColor-normal hover:text-red-500' />
                                <Link
                                  to={`${location.pathname.replace(
                                    'dashboard',
                                    'applications'
                                  )}/${
                                    selectedHowProject?.id
                                  }/application_details`}
                                >
                                  <p className='font-semibold text-primaryColor-normal underline'>
                                    {selectedHowProject?.howName}
                                  </p>
                                </Link>
                                <p className='text-xs'>
                                  {selectedHowProject?.address}
                                </p>
                              </div>
                            )}
                          {howProject && (
                            <LocationMarkerIcon
                              onClick={() => setSelectedHowProject(howProject)}
                              className={
                                selectedHowProject?.id === howProject?.id
                                  ? 'h-8 w-8 text-black'
                                  : 'h-8 w-8 text-red-500 '
                              }
                            />
                          )}
                        </div>
                      </LocationMarker>
                    );
                  })}
                </GoogleMap>
              )}
          </div>
        )}

        {data.length === 0 && (
          <div className='w-full flex justify-center items-center h-screen/2'>
            <div>
              <div className='flex justify-center items-center gap-2 text-primaryColor-normal'>
                <LocationMarkerIcon className='h-8 w-8' />
                <h1>No Projects</h1>
              </div>
              <p>There are no projects to display</p>
            </div>
          </div>
        )}
        <ProjectTableFooter
          pageSize={1}
          page={1}
          perPage={1}
          perPageHandler={perPageHandler}
          pageChangeHandler={onPageChangeHandler}
        />
      </div>
    </section>
  );
}
